import { Box, Typography } from "@mui/material";
import {
  NotificationPreferenceScope,
  NotificationPreferenceSource,
} from "generated-graphql/graphql";

import { PREFERENCE_GRID_COLUMNS } from "./AlertRow";
import { CHANNEL_CELL_SX, channelLabel, provenanceLabel } from "./preferenceProvenance";
import { PreferenceSwitch } from "./PreferenceSwitch";
import { formatSendHour } from "./sendHour";
import {
  draftKey,
  draftValue,
  isParentChannelOn,
  NO_PENDING_KEYS,
  NOTIFICATION_CHANNEL_EMAIL,
  PREFERENCE_ROW_CHANNELS,
  type NotificationPreferenceDraft,
} from "./useNotificationPreferenceDraft";

/**
 * The page-wide channel switches, drawn as one row on the same grid as the
 * notifications beneath them.
 *
 * Each one holds a position of its own and silences its channel across the whole
 * page *without* touching anything underneath: the switches below stay exactly
 * where the reader left them, and turning this back on restores them untouched.
 * That is why it writes one row of its own rather than rewriting every row it
 * covers — see the commentary in `parents.ts` for why the alternative cannot keep
 * the promise.
 *
 * Labelled over the two switch columns and nowhere else. The channels are worth
 * naming — this is the first place a reader meets them, and two bare switches give
 * no clue which is which — but a "Notification" label here would be a header for a
 * row that is not about any one record.
 */

export type OverallChannelRowProps = {
  /** The sentinel each switch holds its position against, one per channel. */
  channelParentSwitches: readonly {
    kind: string;
    channel: string;
    source: NotificationPreferenceSource;
  }[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  /** True while a write is in flight; see {@link PreferenceSwitch}. */
  disabled: boolean;
  /** Toggles whose own save is still in flight; each is disabled until it lands. */
  pendingKeys?: ReadonlySet<string>;
  onToggle: (kind: string, channel: string, enabled: boolean) => void;
  /** The hour the sentence under the description names. */
  sendHourLocal: number;
};

/**
 * What this row is called, in the vocabulary the rest of the page uses. "Parent"
 * is the code's word for these switches; the reader is never shown it.
 */
const OVERALL_LABEL = "All notifications";

/**
 * What the switches promise, which is the one thing about them a reader has to
 * believe: turning a channel off does not lose the choices below it.
 */
const OVERALL_DESCRIPTION = "Turn a channel off everywhere. Your choices below are kept.";

export function OverallChannelRow({
  channelParentSwitches,
  draft,
  scope,
  disabled,
  pendingKeys = NO_PENDING_KEYS,
  onToggle,
  sendHourLocal,
}: OverallChannelRowProps) {
  // Hidden rather than shown while email is off, because there is nothing for it to
  // describe: no mail arrives at any hour. The switch beside it is the way back, and
  // every stored cadence is untouched meanwhile.
  const emailOn = isParentChannelOn(draft, channelParentSwitches, NOTIFICATION_CHANNEL_EMAIL);

  return (
    <Box>
      {/*
        Channel names over their own columns, with the name column left empty. Drawn
        on the same grid rather than positioned, so the labels track the switches
        they name however the columns are sized.
      */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
          alignItems: "flex-end",
          columnGap: 1,
          // Tight to the switches they name. These labels belong to this row alone —
          // the sections carry their own — so the gap that reads as "column header
          // for the table" is wrong here.
          mb: -0.5,
        }}
      >
        <Box />
        {PREFERENCE_ROW_CHANNELS.map((channel) => (
          <Box key={channel} sx={CHANNEL_CELL_SX}>
            <Typography variant="overline" color="text.secondary">
              {channelLabel(channel)}
            </Typography>
          </Box>
        ))}
        {/* Caret lane, reserved so these labels sit over the switches below. */}
        <Box />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
          alignItems: "center",
          columnGap: 1,
          // Space but no rule: the row is a control, not a section boundary, and a
          // line here read as the top edge of the first section rather than the
          // bottom of this row.
          //
          // Matched to the gap an expanded section leaves below itself — its own `mb`
          // plus its details' bottom padding — so this row sits in the same rhythm as
          // the sections rather than closer to the first one than they are to each
          // other.
          pb: 4,
        }}
      >
        <Box>
          <Typography variant="body1" fontWeight={500}>
            {OVERALL_LABEL}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {OVERALL_DESCRIPTION}
          </Typography>
          {/*
            How often is chosen per alert, on the rows below, so this row no longer
            asks. What is left is the one part of the schedule every one of those
            cadences shares: the hour of the morning they go out in.
          */}
          {emailOn && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Summaries arrive around {formatSendHour(sendHourLocal)}, your local time.
            </Typography>
          )}
        </Box>

        {/*
          Generated from the same channel list the rows use rather than written out,
          so a switch can never end up over the column it does not govern. Standard
          size against the small ones on the notification rows: these govern
          everything, and the size difference is what says so.
        */}
        {PREFERENCE_ROW_CHANNELS.map((channel) => {
          const parent = channelParentSwitches.find((entry) => entry.channel === channel);
          if (!parent) {
            return <Box key={channel} sx={CHANNEL_CELL_SX} />;
          }

          return (
            <Box key={channel} sx={CHANNEL_CELL_SX}>
              <PreferenceSwitch
                label={`${channelLabel(channel)} for all notifications`}
                // Where this position came from, exactly as a notification row says
                // it. A parent switch is a stored preference like any other, so an
                // inherited mute has to be attributable to the organization rather
                // than presented as the reader's own choice.
                explanation={provenanceLabel(parent.source, scope)}
                checked={draftValue(draft, parent.kind, parent.channel)}
                disabled={disabled || pendingKeys.has(draftKey(parent.kind, parent.channel))}
                onChange={(enabled) => onToggle(parent.kind, parent.channel, enabled)}
              />
            </Box>
          );
        })}

        {/* Caret lane, empty here. */}
        <Box />
      </Box>
    </Box>
  );
}
